import { useRoute, useRuntimeConfig } from '#imports'
import { type ComputedRef, computed } from 'vue'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { useIsShoppingAssistantWebEnabled } from './useIsShoppingAssistantWebEnabled'

/**
 * Whether the gifting-season festive treatment of the shopping-assistant FAB
 * (falling-ASCII-snowflakes effect) should render.
 *
 * Backed by the `FF_SHOPPING_ASSISTANT_FESTIVE_FAB_ENABLED_COUNTRIES` runtime feature flag —
 * a comma-separated list of country codes, so the rollout can be enabled
 * per country without a deploy (set via the DSE locally, and in
 * front-office-config for preprod/prod). Returns true only for countries
 * listed in the flag, and only where the shopping assistant itself is
 * enabled on the current route/market.
 */
export function useIsShoppingAssistantFabFestive(): ComputedRef<boolean> {
  const route = useRoute()
  const { market } = useMarketplace()
  const { FF_SHOPPING_ASSISTANT_FESTIVE_FAB_ENABLED_COUNTRIES } =
    useRuntimeConfig().public
  const isShoppingAssistantEnabled = useIsShoppingAssistantWebEnabled()

  return computed(() => {
    if (route.meta?.shoppingAssistant?.enabled === false) {
      return false
    }

    const isCountryEnabled = Boolean(
      FF_SHOPPING_ASSISTANT_FESTIVE_FAB_ENABLED_COUNTRIES?.includes(
        market.countryCode,
      ),
    )

    return isShoppingAssistantEnabled.value && isCountryEnabled
  })
}
